<template>
  <div>
    <b-container class="pa-0 ma-3">
      <p>
        <strong>
          Proposal category filter
        </strong>
      </p>

      <extended-checkbox-group v-model="selectedProposalCategories"
                               :options="proposalCategoriesOptions"
                               class="checkbox-margin"/>
    </b-container>

    <b-table :empty-html="constantStrings.emptyDataHtml"
             :fields="fields"
             :items="filteredDocuments"
             :responsive="true"
             :tbody-tr-class="highlightPreviousProposals"
             head-variant="light"
             hover
             show-empty
             striped>

      <template v-slot:cell(ordinalNumber)="row">
        <base-column-ordinal-number v-bind="{index: row.index, perPage: 0, currentPage: 1}"/>
      </template>

      <template v-if="!showMainAuthorProposalsOnly" v-slot:cell(team_with_roles)="row">
        <p v-for="(member, memberIndex) in row.item.team_with_roles" :key="memberIndex" class="mb-0">
          {{ member.first_name }} {{ member.last_name }}
          (<span v-for="(role, roleIndex) in member.roles" :key="role">{{ formatRole(role) }}<span
          v-if="roleIndex < member.roles.length - 1">, </span></span>)
        </p>
      </template>

      <template v-slot:cell(actions)="row">
        <action-download-pdf :proposal="row.item"/>

        <action-download-report-pdf :proposal="row.item"/>

        <action-open-proposal-full-details :proposal="row.item"/>
      </template>

    </b-table>

  </div>
</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import ActionDownloadReportPdf from '@/components/actions/ActionDownloadReportPdf';
  import ActionOpenProposalFullDetails from '@/components/actions/ActionOpenProposalFullDetails';
  import ExtendedCheckboxGroup from '@/components/ExtendedCheckboxGroup';
  import constantStrings from '@/json/constantStrings';
  import { proposalHistory } from '@/mixins/proposalHistory';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import { roleFormatMixin } from '@/mixins/roleFormat';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';

  export default {
    name: 'ProposalsListReadonly',
    components: {
      ActionDownloadReportPdf,
      ActionOpenProposalFullDetails,
      ActionDownloadPdf,
      BaseColumnOrdinalNumber,
      ExtendedCheckboxGroup,
    },
    props: {
      proposalState: {
        type: String,
        default: 'submitted',
      },
      userIds: {
        type: [String, Array],
        required: true,
      },
      excludedId: {
        type: String,
        default: '',
      },
      previousProposalsPointer: {
        type: [String, Array],
        default: '',
      },
    },
    mixins: [proposalHistory, infrastructureMixin, roleFormatMixin],
    data() {
      return {
        constantStrings,
        documents: [],
        selectedProposalCategories: ['new', 'continuation', 'resubmission', 'ceric'],
        proposalCategoriesOptions: [],
        previousProposalsIds: [],
        fields: [],
      };
    },
    computed: {
      showMainAuthorProposalsOnly() {
        return typeof this.userIds === 'string';
      },
      isCategoryFilterApplicable() {
        return this.infrastructure === 'synchrotron';
      },
      filteredDocuments() {
        if (this.isCategoryFilterApplicable) {
          return this.documents.filter((document) => {
            const documentCategory = this.getDocumentCategory(document);
            return this.selectedProposalCategories.includes(documentCategory);
          });
        }
        return this.documents;
      },
    },
    methods: {
      loadData() {
        const request = this.showMainAuthorProposalsOnly
          ? this.axios.get(`documents/all-infrastructures/${this.proposalState}/${this.userIds}`)
          : this.axios.get(
            `/documents/all-infrastructures/${this.proposalState}/team`,
            // eslint-disable-next-line camelcase
            { params: { team_ids: this.userIds } },
          );
        request
          .then((response) => {
            this.prepareDocuments(response.data.documents);
          });
      },
      prepareDocuments(documents) {
        this.documents = documents
          .filter((el) => this.getId(el) !== this.excludedId)
          .reduce((acc, el) => {
            if (!acc.some((document) => this.getId(document) === this.getId(el))) {
              acc.push(el);
            }
            return acc;
          }, [])
          .reverse();
      },
      getDocumentCategory(document) {
        const { subtype = [] } = document.document_specification;
        return 'ceric' in subtype ? 'ceric' : document.general.category?.toLowerCase();
      },
      constructProposalCategoriesOptions() {
        this.proposalCategoriesOptions = this.selectedProposalCategories.map(
          (category) => ({
            text: this.capitalizeText(category),
            name: category,
            buttonVariant: 'outline-info',
          }),
        );
      },
      async loadPreviousProposalsIds() {
        const previousProposals = await this.fetchPreviousProposals(this.previousProposalsPointer);
        this.previousProposalsIds = previousProposals.map((proposal) => this.getId(proposal));
      },
      highlightPreviousProposals(item, type) {
        if (item && type === 'row' && this.previousProposalsIds.includes(this.getId(item))) {
          return 'table-success';
        }
        return '';
      },
    },
    async created() {
      const helpers = await this.getEvaluationDashboardProperties(this.infrastructure, 'FinalGrade');
      this.fields = helpers.getSingleField(this.showMainAuthorProposalsOnly
        ? 'finalGradeFinalGradesDetailsUserProposals'
        : 'finalGradeFinalGradesDetailsTeamProposals');
      await this.loadData();
      this.constructProposalCategoriesOptions();
      await this.loadPreviousProposalsIds();
    },
  };
</script>

<style scoped>
  /* Modifying bootstrap class to delete ugly bottom white bar */
  .table-responsive {
    margin-bottom: 0;
  }

  .checkbox-margin {
    margin-bottom: 0;
  }
</style>
